import React, { useState, useEffect } from 'react';
import { DateBox } from 'devextreme-react';
import { NumericFormat } from 'react-number-format';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import * as Store from '@apply/store';
import MaskedInput from 'react-text-mask';
import { stateSelectorValues, countyList, Accordion, YearInput } from '@apply/ui';
import { alertModal, ValidationMessage } from '@apply/ui';
import {
    ExperienceType,
    ExperienceProjectType,
    ExperienceProjectTypeOptions,
    ExperiencePropertyType,
    ExperiencePropertyTypeOptions,
    ExperiencePropertyTwoToFourUnitStructureType,
    ExperiencePropertyTwoToFourUnitStructureTypeOptions,
    ExperiencePropertyTypeCategory,
    ExperienceVerificationStatusType,
    ExperienceDateType,
} from '@apply/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons';

export interface PrincipalExperiencePropertyDetailModalProps {
    isOpen: boolean;
    onCloseCallback?: (tempfileName: string) => void;
    experience?: Store.UserStore.Types.UserExperience;
    initialExperienceCreate: boolean;
}

export const PrincipalExperiencePropertyDetailModal: React.FC<PrincipalExperiencePropertyDetailModalProps> = ({
    isOpen,
    onCloseCallback,
    experience,
    initialExperienceCreate,
}) => {
    const [canContinue, set_canContinue] = useState(true);
    const [experienceId, set_experienceId] = useState(null);
    const [principalId, set_principalId] = useState(null);
    const [projectTypeId, set_projectTypeId] = useState(null);
    const [homeConstructedAfterPurchase, set_homeConstructedAfterPurchase] = useState<boolean>(null);
    const [propertyTypeId, set_propertyTypeId] = useState(null);
    const [twoToFourUnitTypeId, set_twoToFourUnitTypeId] = useState(null);
    const [experienceTypeId, set_experienceTypeId] = useState(ExperienceType.NonBfi);
    const [unitCount, set_unitCount] = useState(null);
    const [street1, set_addressStreet1] = useState('');
    const [street2, set_addressStreet2] = useState('');
    const [city, set_addressCity] = useState('');
    const [state, set_addressState] = useState('');
    const [zipCode, set_addressZipCode] = useState('');
    const [county, set_addressCounty] = useState('');
    const [stillOwn, set_stillOwn] = useState(false);
    const [dateSold, set_dateSold] = useState(null);
    const [dateSoldTypeId, set_dateSoldTypeId] = useState(ExperienceDateType.Exact);
    const [salesPrice, set_salesPrice] = useState(null);
    const [budget, set_budget] = useState(null);
    const [lender, set_lender] = useState('');
    const [isVerified, set_isVerified] = useState(false);
    const [commercialConsiderationAcknowledged, set_commercialConsiderationAcknowledged] = useState<boolean>(null);
    const [verificationStatusTypeId, set_verificationStatusTypeId] = useState(ExperienceVerificationStatusType.Submitted);

    const updateUserExperience = async (experience) => await Store.CrossStore.saveUserExperienceItem(experience);

    const validateUserExperience = async (experience) => await Store.CrossStore.validateUserExperience(experience);

    const [countySelectList, setCountySelectList] = useState<{ value: string; display: string }[]>([]);

    const [showSupplementalInfo, set_experienceShowSupplementalInfo] = useState<boolean>(false);

    useEffect(() => {
        const tempCountyList = countyList
            .filter((county) => county.state === state)
            .map((county) => {
                return { display: county.county, value: county.county };
            });
        tempCountyList.unshift({ display: '', value: '' });
        setCountySelectList(tempCountyList);
    }, [state]);

    useEffect(() => {
        resetState();
        setState(experience);
    }, [experience]);

    useEffect(() => {
        let fieldsValid = false;

        fieldsValid =
            ((projectTypeId !== null &&
                projectTypeId !== ExperienceProjectType.FixFlipRehab &&
                projectTypeId !== ExperienceProjectType.PersonalResidence) ||
                (projectTypeId === ExperienceProjectType.FixFlipRehab && budget !== null && budget !== '') ||
                (projectTypeId === ExperienceProjectType.PersonalResidence && homeConstructedAfterPurchase === true)) &&
            street1 !== '' &&
            city !== '' &&
            state !== '' &&
            zipCode !== '' &&
            (propertyTypeId !== ExperiencePropertyType.Commercial ||
                (propertyTypeId === ExperiencePropertyType.Commercial && commercialConsiderationAcknowledged === true));

        set_canContinue(fieldsValid);
    }, [
        projectTypeId,
        street1,
        city,
        state,
        zipCode,
        budget,
        homeConstructedAfterPurchase,
        propertyTypeId,
        commercialConsiderationAcknowledged,
    ]);

    const setState = (experience) => {
        if (!experience) {
            return;
        }
        set_verificationStatusTypeId(experience.verificationStatusTypeId);
        set_experienceId(experience.id);
        set_principalId(experience.principalId);
        set_experienceTypeId(experience.experienceTypeId);
        set_projectTypeId(experience.projectTypeId);
        set_homeConstructedAfterPurchase(experience.homeConstructedAfterPurchase);
        set_propertyTypeId(experience.propertyTypeId);
        set_twoToFourUnitTypeId(experience.twoToFourUnitTypeId);
        set_unitCount(experience.unitCount);
        set_addressStreet1(experience.address.street1);
        set_addressStreet2(experience.address.street2);
        set_addressCity(experience.address.city);
        set_addressState(experience.address.state);
        set_addressZipCode(experience.address.zipCode);
        set_addressCounty(experience.address.county);
        set_stillOwn(experience.stillOwn);
        set_dateSold(experience.dateSold ? new Date(experience.dateSold).toISOString().split('T')[0] : null);
        set_dateSoldTypeId(experience.dateSoldTypeId ? experience.dateSoldTypeId : ExperienceDateType.Exact);
        set_salesPrice(experience.salesPrice ?? null);
        set_budget(experience.budget);
        set_lender(experience.lender);
        set_commercialConsiderationAcknowledged(experience.commercialConsiderationAcknowledged);
        set_isVerified(experience.isVerified);
    };

    const resetState = () => {
        set_verificationStatusTypeId(ExperienceVerificationStatusType.Submitted);
        set_experienceId(null);
        set_principalId(null);
        set_experienceTypeId(ExperienceType.NonBfi);
        set_projectTypeId(null);
        set_homeConstructedAfterPurchase(null);
        set_propertyTypeId(null);
        set_twoToFourUnitTypeId(null);
        set_unitCount('');
        set_addressStreet1('');
        set_addressStreet2('');
        set_addressCity('');
        set_addressState('');
        set_addressZipCode('');
        set_addressCounty('');
        set_stillOwn(false);
        set_dateSold(null);
        set_dateSoldTypeId(ExperienceDateType.Exact);
        set_salesPrice(null);
        set_budget(null);
        set_lender('');
        set_commercialConsiderationAcknowledged(null);
        set_isVerified(false);
        set_experienceShowSupplementalInfo(false);
    };

    const onHomeConstructedAfterPurchaseChange = (value) => {
        set_homeConstructedAfterPurchase(value);
    };

    const onProjectTypeChange = (id) => {
        set_budget(null);
        set_homeConstructedAfterPurchase(null);
        set_projectTypeId(id);
    };

    const onPropertyTypeChange = (id) => {
        let unitCount = null;
        switch (id) {
            case ExperiencePropertyType.DetachedHouse:
            case ExperiencePropertyType.Townhouse:
            case ExperiencePropertyType.CondoUnit:
                unitCount = 1;
                break;
        }
        set_unitCount(unitCount);
        set_commercialConsiderationAcknowledged(null);
        set_twoToFourUnitTypeId(null);

        set_propertyTypeId(id);
    };

    const onPropertyTwoToFourUnitTypeChange = (id) => {
        let unitCount = null;
        switch (id) {
            case ExperiencePropertyTwoToFourUnitStructureType.Two:
                unitCount = 2;
                break;
            case ExperiencePropertyTwoToFourUnitStructureType.Three:
                unitCount = 3;
                break;
            case ExperiencePropertyTwoToFourUnitStructureType.Four:
                unitCount = 4;
                break;
        }
        set_unitCount(unitCount);

        set_twoToFourUnitTypeId(id);
    };

    const onBudgetSet = (value) => {
        set_budget(value);
    };

    const onYearSelection = (value) => {
        if (value && value !== '' && value != null) {
            const selectedDate = new Date(value).toISOString().split('T')[0];
            set_dateSold(selectedDate);
        } else {
            set_dateSold(null);
        }
    };

    const onDateSoldTypeChange = (value) => {
        set_dateSold(null);
        set_dateSoldTypeId(value);
    };

    const onStillOwnChanged = (val) => {
        set_stillOwn(val);
        if (val === true) {
            set_dateSold(null);
            set_salesPrice(null);
        }
    };

    const getSuccessMessage = (id) => {
        if (id === 0) {
            return 'Your property was added successfully.';
        } else {
            return 'Your property was updated successfully';
        }
    };

    const getGeneralErrorMessage = () => {
        return 'An error has occurred while communicating with our server.  Please try your operation again.';
    };

    const handleClose = () => {
        resetState();
        onCloseCallback(null);
    };

    const validateExperience = async () => {
        try {
            const model = {
                id: experienceId ?? 0,
                principalId: principalId ?? 0,
                address: {
                    street1,
                    street2,
                    city,
                    state,
                    zipCode,
                },
            };

            const result = await validateUserExperience(model);
            return result;
        } catch (ex) {
            alertModal({
                message: getGeneralErrorMessage(),
            });
        }
    };

    const saveUserExperience = async () => {
        const isValid = await validateExperience();
        if (!isValid) {
            alertModal({
                message: 'We are unable to accept this property, you have already submitted a property with this address.',
            });
            return;
        }

        const model = {
            id: experienceId ?? 0,
            projectTypeId,
            experienceTypeId,
            propertyTypeId,
            twoToFourUnitTypeId,
            lender,
            principalId: principalId ?? 0,
            address: {
                street1,
                street2,
                city,
                state,
                zipCode,
                county,
            },
            stillOwn,
            dateSold: dateSold ? new Date(dateSold).toISOString().split('T')[0] : null,
            dateSoldTypeId,
            salesPrice: salesPrice ? parseFloat(salesPrice) : null,
            budget: budget ? parseFloat(budget) : null,
            unitCount: unitCount,
            isVerified,
            homeConstructedAfterPurchase,
            commercialConsiderationAcknowledged,
            verificationStatusTypeId: verificationStatusTypeId,
        };
        const result = await updateUserExperience(model);

        if (!initialExperienceCreate || (initialExperienceCreate && showSupplementalInfo)) {
            if (result.success === true) {
                alertModal({
                    title: 'Success',
                    message: getSuccessMessage(model.id),
                    onConfirm: () => {
                        handleClose();
                    },
                });
            } else {
                alertModal({
                    message: getGeneralErrorMessage(),
                });
            }
        } else {
            if (result.success === true) {
                set_experienceId(result.id);
                set_principalId(result.principalId);
                set_experienceShowSupplementalInfo(true);
            } else {
                alertModal({
                    message: getGeneralErrorMessage(),
                });
            }
        }
    };

    const styles = {
        button: {
            width: 175,
        },
        noPadding: {
            padding: 0,
        },
        checkbox: {
            marginRight: '5px',
        },
    };

    return (
        <div>
            <Dialog
                open={isOpen}
                aria-labelledby='alert-dialog-title'
                aria-describedby='alert-dialog-description'
                maxWidth='md'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            role='dialog'
                            style={{ maxWidth: '80vw' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <div
                                    className='page-header'
                                    role='banner'
                                >
                                    <h1>{!experience || experience?.id === 0 ? 'Add Experience' : 'Edit Experience'}</h1>
                                    <button
                                        type='button'
                                        className='close'
                                        data-dismiss='modal'
                                        aria-label='Close'
                                        onClick={handleClose}
                                    >
                                        <span aria-hidden='true'>&times;</span>
                                    </button>
                                </div>
                                <div className='modal-content '>
                                    <div className='modal-body'>
                                        <div
                                            className='standard-form'
                                            style={styles.noPadding}
                                        >
                                            <div
                                                className='scrollable-content'
                                                style={{ margin: '10px' }}
                                            >
                                                <div
                                                    className='accordion'
                                                    data-accordion=''
                                                    data-allow-all-closed='true'
                                                    data-multi-expand='true'
                                                >
                                                    {(!initialExperienceCreate ||
                                                        (initialExperienceCreate && showSupplementalInfo === true)) && (
                                                        <Accordion
                                                            title={<h3>Additional Information</h3>}
                                                            isCollapsable={!initialExperienceCreate}
                                                        >
                                                            <div
                                                                style={{
                                                                    border: '1px solid lightgray',
                                                                    background: '#F8F7F7',
                                                                    borderRadius: '5px',
                                                                    padding: '5px',
                                                                    marginBottom: '15px',
                                                                }}
                                                            >
                                                                <div className='row'>
                                                                    <div className='columns small-12 large-5'>
                                                                        <label htmlFor='propertyTypeId'>Property Type</label>
                                                                        <select
                                                                            id={'propertyTypeId'}
                                                                            value={propertyTypeId ?? ''}
                                                                            onChange={(event) =>
                                                                                onPropertyTypeChange(parseInt(event.target.value))
                                                                            }
                                                                            disabled={experienceTypeId !== ExperienceType.NonBfi}
                                                                        >
                                                                            <option value=''></option>
                                                                            {ExperiencePropertyTypeOptions.filter(
                                                                                (item) =>
                                                                                    item.category ===
                                                                                    ExperiencePropertyTypeCategory.CoreOptions
                                                                            ).map((o) => {
                                                                                return (
                                                                                    <option
                                                                                        key={o.id}
                                                                                        value={o.id}
                                                                                    >
                                                                                        {o.name}
                                                                                    </option>
                                                                                );
                                                                            })}
                                                                            <option disabled={true}>
                                                                                ──────────────────────
                                                                            </option>
                                                                            {ExperiencePropertyTypeOptions.filter(
                                                                                (item) =>
                                                                                    item.category ===
                                                                                    ExperiencePropertyTypeCategory.Other
                                                                            ).map((o) => {
                                                                                return (
                                                                                    <option
                                                                                        key={o.id}
                                                                                        value={o.id}
                                                                                    >
                                                                                        {o.name}
                                                                                    </option>
                                                                                );
                                                                            })}
                                                                        </select>
                                                                    </div>
                                                                    {propertyTypeId ===
                                                                        ExperiencePropertyType.TwoToFourUnitStructure && (
                                                                        <div className='columns small-12 large-3'>
                                                                            <label htmlFor='twoToFourUnitTypeId'>
                                                                                2-4 Unit Structure Type
                                                                            </label>
                                                                            <select
                                                                                id={'twoToFourUnitTypeId'}
                                                                                value={twoToFourUnitTypeId ?? ''}
                                                                                onChange={(event) =>
                                                                                    onPropertyTwoToFourUnitTypeChange(
                                                                                        parseInt(event.target.value)
                                                                                    )
                                                                                }
                                                                                disabled={
                                                                                    experienceTypeId !== ExperienceType.NonBfi
                                                                                }
                                                                            >
                                                                                <option value=''></option>
                                                                                {ExperiencePropertyTwoToFourUnitStructureTypeOptions.map(
                                                                                    (o) => {
                                                                                        return (
                                                                                            <option
                                                                                                key={o.id}
                                                                                                value={o.id}
                                                                                            >
                                                                                                {o.name}
                                                                                            </option>
                                                                                        );
                                                                                    }
                                                                                )}
                                                                            </select>
                                                                        </div>
                                                                    )}
                                                                    {(propertyTypeId ===
                                                                        ExperiencePropertyType.FivePlusMultifamily ||
                                                                        propertyTypeId ===
                                                                            ExperiencePropertyType.ResidentialMixedUse) && (
                                                                        <div className='columns small-12 large-2'>
                                                                            <label htmlFor='unitCount'>Units</label>
                                                                            <NumericFormat
                                                                                id='unitCount'
                                                                                value={unitCount ?? ''}
                                                                                decimalScale={0}
                                                                                allowNegative={false}
                                                                                onValueChange={(values) => {
                                                                                    set_unitCount(values.floatValue);
                                                                                }}
                                                                                isAllowed={(values) => {
                                                                                    const value = values?.floatValue;
                                                                                    return (
                                                                                        value === undefined ||
                                                                                        value === null ||
                                                                                        (propertyTypeId !==
                                                                                            ExperiencePropertyType.FivePlusMultifamily &&
                                                                                            value >= 0 &&
                                                                                            value <= 100) ||
                                                                                        (propertyTypeId ===
                                                                                            ExperiencePropertyType.FivePlusMultifamily &&
                                                                                            value >= 5 &&
                                                                                            value <= 100)
                                                                                    );
                                                                                }}
                                                                                disabled={
                                                                                    experienceTypeId !== ExperienceType.NonBfi ||
                                                                                    (propertyTypeId !==
                                                                                        ExperiencePropertyType.FivePlusMultifamily &&
                                                                                        propertyTypeId !==
                                                                                            ExperiencePropertyType.ResidentialMixedUse)
                                                                                }
                                                                            />
                                                                        </div>
                                                                    )}
                                                                    {propertyTypeId === ExperiencePropertyType.Commercial && (
                                                                        <div className='columns small-12 large-6'>
                                                                            <label>
                                                                                NOTE: Commercial Experience May Not Be Considered
                                                                            </label>
                                                                            <input
                                                                                id='commercialConsiderationAcknowledged'
                                                                                type='checkbox'
                                                                                onChange={(event) =>
                                                                                    set_commercialConsiderationAcknowledged(
                                                                                        event.target.checked
                                                                                    )
                                                                                }
                                                                                checked={commercialConsiderationAcknowledged}
                                                                                style={styles.checkbox}
                                                                            />
                                                                            <label htmlFor='commercialConsiderationAcknowledged'>
                                                                                I Understand
                                                                            </label>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className='row'>
                                                                    <div className='columns small-12 large-5'>
                                                                        <label htmlFor='homeConstructedAfterPurchase'>
                                                                            Do you still currently own the property?
                                                                        </label>
                                                                        <div
                                                                            style={{
                                                                                float: 'left',
                                                                            }}
                                                                        >
                                                                            <label
                                                                                htmlFor='stillOwnProperty-yes'
                                                                                style={{
                                                                                    float: 'left',
                                                                                }}
                                                                            >
                                                                                <input
                                                                                    type='radio'
                                                                                    id='stillOwnProperty-yes'
                                                                                    checked={stillOwn === true}
                                                                                    onChange={() => onStillOwnChanged(true)}
                                                                                />
                                                                                Yes
                                                                            </label>
                                                                            <label
                                                                                htmlFor='stillOwnProperty-no'
                                                                                style={{
                                                                                    float: 'left',
                                                                                }}
                                                                            >
                                                                                <input
                                                                                    type='radio'
                                                                                    id='stillOwnProperty-no'
                                                                                    checked={stillOwn === false}
                                                                                    onChange={() => onStillOwnChanged(false)}
                                                                                />
                                                                                No
                                                                            </label>
                                                                        </div>
                                                                        {projectTypeId === ExperienceProjectType.Construction &&
                                                                            stillOwn && (
                                                                                <div
                                                                                    style={{
                                                                                        width: '100%',
                                                                                        float: 'left',
                                                                                        textAlign: 'left',
                                                                                    }}
                                                                                >
                                                                                    <ValidationMessage>
                                                                                        Please ensure this shouldn't be a Rental
                                                                                        Property
                                                                                    </ValidationMessage>
                                                                                </div>
                                                                            )}
                                                                    </div>
                                                                    <div className='columns small-12 large-3'>
                                                                        <label htmlFor='dateSold'>Date Sold</label>
                                                                        {dateSoldTypeId === ExperienceDateType.Exact && (
                                                                            <DateBox
                                                                                id='dateSold'
                                                                                value={dateSold}
                                                                                data-testid='dateSold'
                                                                                disabled={stillOwn}
                                                                                onValueChange={set_dateSold}
                                                                                type='date'
                                                                                style={{
                                                                                    height: '1.9375rem',
                                                                                }}
                                                                            />
                                                                        )}
                                                                        {dateSoldTypeId === ExperienceDateType.YearOnly && (
                                                                            <YearInput
                                                                                id='dateSold'
                                                                                value={
                                                                                    dateSold
                                                                                        ? new Date(dateSold).getUTCFullYear()
                                                                                        : null
                                                                                }
                                                                                disabled={stillOwn}
                                                                                onChange={(event) =>
                                                                                    onYearSelection(event.target.value)
                                                                                }
                                                                                maxWidth='190px'
                                                                            />
                                                                        )}
                                                                        <div
                                                                            style={{
                                                                                paddingTop: '5px',
                                                                            }}
                                                                        >
                                                                            <label
                                                                                htmlFor='dateSoldType-exact'
                                                                                style={{
                                                                                    float: 'left',
                                                                                }}
                                                                            >
                                                                                <input
                                                                                    type='radio'
                                                                                    id='dateSoldType-exact'
                                                                                    disabled={stillOwn}
                                                                                    checked={
                                                                                        dateSoldTypeId ===
                                                                                        ExperienceDateType.Exact
                                                                                    }
                                                                                    onChange={() =>
                                                                                        onDateSoldTypeChange(
                                                                                            ExperienceDateType.Exact
                                                                                        )
                                                                                    }
                                                                                />
                                                                                Exact Date
                                                                            </label>
                                                                            <label
                                                                                htmlFor='dateSoldType-year'
                                                                                style={{
                                                                                    float: 'left',
                                                                                }}
                                                                            >
                                                                                <input
                                                                                    type='radio'
                                                                                    id='dateSoldType-year'
                                                                                    disabled={stillOwn}
                                                                                    checked={
                                                                                        dateSoldTypeId ===
                                                                                        ExperienceDateType.YearOnly
                                                                                    }
                                                                                    onChange={() =>
                                                                                        onDateSoldTypeChange(
                                                                                            ExperienceDateType.YearOnly
                                                                                        )
                                                                                    }
                                                                                />
                                                                                Year Only
                                                                            </label>
                                                                        </div>
                                                                    </div>
                                                                    <div className='columns small-12 large-3'>
                                                                        <label htmlFor='salesPrice'>Sales Price</label>
                                                                        <NumericFormat
                                                                            id='salesPrice'
                                                                            value={salesPrice ?? ''}
                                                                            decimalScale={2}
                                                                            prefix='$'
                                                                            thousandSeparator=','
                                                                            allowNegative={false}
                                                                            onValueChange={(values) => {
                                                                                set_salesPrice(values.floatValue);
                                                                            }}
                                                                            isAllowed={(values) => {
                                                                                const value = values?.floatValue;
                                                                                return (
                                                                                    value === undefined ||
                                                                                    value === null ||
                                                                                    (value >= 0 && value <= 100000000)
                                                                                );
                                                                            }}
                                                                            disabled={stillOwn}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                {experienceTypeId === ExperienceType.NonBfi && (
                                                                    <div className='row'>
                                                                        <div className='columns small-12 large-12'>
                                                                            <label htmlFor='lender'>
                                                                                Lender
                                                                                <span className='helper'>
                                                                                    <span className='tooltip-trigger'>
                                                                                        <FontAwesomeIcon
                                                                                            icon={faCircleQuestion}
                                                                                        />
                                                                                    </span>
                                                                                    <span className='text'>
                                                                                        Current or more recent lender.
                                                                                    </span>
                                                                                </span>
                                                                            </label>
                                                                            <input
                                                                                id='lender'
                                                                                type='text'
                                                                                maxLength={100}
                                                                                value={lender}
                                                                                onChange={(event) =>
                                                                                    set_lender(event.target.value)
                                                                                }
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </Accordion>
                                                    )}
                                                    {((initialExperienceCreate && !showSupplementalInfo) ||
                                                        !initialExperienceCreate) && (
                                                        <Accordion
                                                            title={<h3>Property</h3>}
                                                            isExpanded={initialExperienceCreate && !showSupplementalInfo}
                                                            isCollapsable={!initialExperienceCreate}
                                                        >
                                                            <div
                                                                style={{
                                                                    border: '1px solid lightgray',
                                                                    background: '#F8F7F7',
                                                                    borderRadius: '5px',
                                                                    padding: '5px',
                                                                }}
                                                            >
                                                                <div className='row'>
                                                                    <div className='columns small-12 large-6'>
                                                                        <label htmlFor='creditTypeId'>
                                                                            Experience Project Type <span className='req'>*</span>
                                                                            <span className='helper'>
                                                                                <span className='tooltip-trigger'>
                                                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                                                </span>
                                                                                <span className='text'>
                                                                                    Select one that best describes your experience
                                                                                    with the property
                                                                                </span>
                                                                            </span>
                                                                        </label>
                                                                        <select
                                                                            id={'projectTypeId'}
                                                                            value={projectTypeId ?? ''}
                                                                            onChange={(event) =>
                                                                                onProjectTypeChange(parseInt(event.target.value))
                                                                            }
                                                                            required={true}
                                                                            disabled={experienceTypeId !== ExperienceType.NonBfi}
                                                                        >
                                                                            <option value=''></option>
                                                                            {ExperienceProjectTypeOptions.map((o) => {
                                                                                return (
                                                                                    <option
                                                                                        key={o.id}
                                                                                        value={o.id}
                                                                                    >
                                                                                        {o.name}
                                                                                    </option>
                                                                                );
                                                                            })}
                                                                        </select>
                                                                    </div>
                                                                    {projectTypeId === ExperienceProjectType.FixFlipRehab && (
                                                                        <div className='columns small-12 large-5'>
                                                                            <label htmlFor='budget'>
                                                                                How much did you spend on rehabilitation?{' '}
                                                                                <span className='req'>*</span>
                                                                            </label>
                                                                            <NumericFormat
                                                                                id='budget'
                                                                                value={budget ?? ''}
                                                                                decimalScale={2}
                                                                                prefix='$'
                                                                                thousandSeparator=','
                                                                                allowNegative={false}
                                                                                onValueChange={(values) => {
                                                                                    onBudgetSet(values.floatValue);
                                                                                }}
                                                                                required={true}
                                                                                isAllowed={(values) => {
                                                                                    const value = values?.floatValue;
                                                                                    return (
                                                                                        value === undefined ||
                                                                                        value === null ||
                                                                                        (value >= 0 && value <= 100000000)
                                                                                    );
                                                                                }}
                                                                                disabled={
                                                                                    experienceTypeId !== ExperienceType.NonBfi
                                                                                }
                                                                            />
                                                                        </div>
                                                                    )}
                                                                    {projectTypeId ===
                                                                        ExperienceProjectType.PersonalResidence && (
                                                                        <div
                                                                            className='columns small-12 large-6'
                                                                            style={{
                                                                                display: 'flex',
                                                                            }}
                                                                        >
                                                                            <fieldset>
                                                                                <div
                                                                                    style={{
                                                                                        width: 'calc(100% - 160px)',
                                                                                        float: 'left',
                                                                                    }}
                                                                                >
                                                                                    <label
                                                                                        htmlFor='homeConstructedAfterPurchase'
                                                                                        style={{
                                                                                            paddingRight: '0px !important',
                                                                                        }}
                                                                                    >
                                                                                        After you closed on your purchase was the
                                                                                        home constructed?{' '}
                                                                                        <span className='req'>*</span>
                                                                                    </label>
                                                                                </div>
                                                                                <div
                                                                                    style={{
                                                                                        width: '160px',
                                                                                        float: 'right',
                                                                                    }}
                                                                                >
                                                                                    <div className='yes-no'>
                                                                                        <label htmlFor='homeConstructedAfterPurchase-yes'>
                                                                                            <input
                                                                                                type='radio'
                                                                                                id='homeConstructedAfterPurchase-yes'
                                                                                                checked={
                                                                                                    homeConstructedAfterPurchase ===
                                                                                                    true
                                                                                                }
                                                                                                onChange={() =>
                                                                                                    onHomeConstructedAfterPurchaseChange(
                                                                                                        true
                                                                                                    )
                                                                                                }
                                                                                            />
                                                                                            Yes
                                                                                        </label>
                                                                                        <label htmlFor='homeConstructedAfterPurchase-no'>
                                                                                            <input
                                                                                                type='radio'
                                                                                                id='homeConstructedAfterPurchase-no'
                                                                                                checked={
                                                                                                    homeConstructedAfterPurchase ===
                                                                                                    false
                                                                                                }
                                                                                                onChange={() =>
                                                                                                    onHomeConstructedAfterPurchaseChange(
                                                                                                        false
                                                                                                    )
                                                                                                }
                                                                                            />
                                                                                            No
                                                                                        </label>
                                                                                    </div>
                                                                                </div>
                                                                                {homeConstructedAfterPurchase != null &&
                                                                                    !homeConstructedAfterPurchase && (
                                                                                        <div
                                                                                            style={{
                                                                                                width: '100%',
                                                                                                float: 'left',
                                                                                                textAlign: 'left',
                                                                                            }}
                                                                                        >
                                                                                            <ValidationMessage>
                                                                                                This type of experience is not
                                                                                                eligible.
                                                                                            </ValidationMessage>
                                                                                        </div>
                                                                                    )}
                                                                            </fieldset>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className='row'>
                                                                    <div className='columns small-12 large-8'>
                                                                        <label htmlFor='street1'>
                                                                            Street Address <span className='req'>*</span>
                                                                        </label>
                                                                        <input
                                                                            id='street1'
                                                                            type='text'
                                                                            maxLength={100}
                                                                            value={street1}
                                                                            onChange={(event) =>
                                                                                set_addressStreet1(event.target.value)
                                                                            }
                                                                            required={true}
                                                                            disabled={experienceTypeId !== ExperienceType.NonBfi}
                                                                        />
                                                                    </div>
                                                                    <div className='columns small-12 large-4'>
                                                                        <label htmlFor='street2'>Street Address 2</label>
                                                                        <input
                                                                            id='street2'
                                                                            type='text'
                                                                            maxLength={100}
                                                                            value={street2}
                                                                            onChange={(event) =>
                                                                                set_addressStreet2(event.target.value)
                                                                            }
                                                                            disabled={experienceTypeId !== ExperienceType.NonBfi}
                                                                        />
                                                                    </div>
                                                                    <div className='columns small-12 large-6'>
                                                                        <label htmlFor='city'>
                                                                            City <span className='req'>*</span>
                                                                        </label>
                                                                        <input
                                                                            id='city'
                                                                            type='text'
                                                                            maxLength={50}
                                                                            value={city}
                                                                            onChange={(event) =>
                                                                                set_addressCity(event.target.value)
                                                                            }
                                                                            required={true}
                                                                            disabled={experienceTypeId !== ExperienceType.NonBfi}
                                                                        />
                                                                    </div>
                                                                    <div className='columns small-12 medium-6 large-3'>
                                                                        <label htmlFor='state'>
                                                                            State <span className='req'>*</span>
                                                                        </label>
                                                                        <select
                                                                            id={'state'}
                                                                            value={state ?? ''}
                                                                            onChange={(event) =>
                                                                                set_addressState(event.target.value)
                                                                            }
                                                                            required={true}
                                                                            disabled={experienceTypeId !== ExperienceType.NonBfi}
                                                                        >
                                                                            {stateSelectorValues.map((v, i) => {
                                                                                return (
                                                                                    <option
                                                                                        key={i}
                                                                                        value={v.value}
                                                                                    >
                                                                                        {v.display}
                                                                                    </option>
                                                                                );
                                                                            })}
                                                                        </select>
                                                                    </div>
                                                                    <div className='columns small-12 medium-6 large-3'>
                                                                        <label htmlFor='zipCode'>
                                                                            Zip Code <span className='req'>*</span>
                                                                        </label>
                                                                        <MaskedInput
                                                                            id='zipCode'
                                                                            mask={[/\d/, /\d/, /\d/, /\d/, /\d/]}
                                                                            guide={true}
                                                                            type='text'
                                                                            value={zipCode}
                                                                            onChange={(event) =>
                                                                                set_addressZipCode(event.target.value)
                                                                            }
                                                                            required
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className='row'>
                                                                    <div className='columns small-12 large-4'>
                                                                        <label htmlFor={'county'}>County</label>
                                                                        <select
                                                                            id={'county'}
                                                                            value={county ?? ''}
                                                                            onChange={(event) =>
                                                                                set_addressCounty(event.target.value)
                                                                            }
                                                                            required={true}
                                                                            disabled={experienceTypeId !== ExperienceType.NonBfi}
                                                                        >
                                                                            {countySelectList.map((v, i) => {
                                                                                return (
                                                                                    <option
                                                                                        key={i}
                                                                                        value={v.value}
                                                                                    >
                                                                                        {v.display}
                                                                                    </option>
                                                                                );
                                                                            })}
                                                                        </select>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </Accordion>
                                                    )}
                                                </div>
                                            </div>
                                            <div className='modal-content-actions'>
                                                <button
                                                    type='button'
                                                    data-testid='accept-button'
                                                    className='button primary'
                                                    style={styles.button}
                                                    onClick={saveUserExperience}
                                                    disabled={!canContinue}
                                                >
                                                    {!initialExperienceCreate || (initialExperienceCreate && showSupplementalInfo)
                                                        ? 'Submit'
                                                        : 'Next'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions></DialogActions>
            </Dialog>
        </div>
    );
};
